 import React, { useState, useEffect } from "react";
import { sliderService } from "../services/sliderService";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://newgardenbackend.amcfest.in/api";

const SliderManagement = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
    link: "",
    order: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [viewImageModal, setViewImageModal] = useState({
    show: false,
    src: "",
    title: "",
  });

  // Local placeholder image (SVG data URI)
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
  const placeholderImageLarge = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236b7280'%3EImage Not Found%3C/text%3E%3C/svg%3E";

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // Replace localhost URLs with production URL
    if (imagePath.includes("localhost:4001")) {
      imagePath = imagePath.replace(/http:\/\/localhost:4001/g, "https://newgardenbackend.amcfest.in");
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    // Get base URL without /api
    const baseUrl = API_BASE_URL.replace("/api", "");
    // If it starts with /, it's a relative path from the base
    if (imagePath.startsWith("/")) {
      return `${baseUrl}${imagePath}`;
    }
    // Otherwise, assume it's relative to the base
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    loadSliders();
  }, []);

  const loadSliders = async () => {
    try {
      setLoading(true);
      const data = await sliderService.getAllSliders();
      console.log("Sliders data:", data);
      const slidersArray = Array.isArray(data)
        ? data
        : data?.data && Array.isArray(data.data)
        ? data.data
        : [];
      console.log("Processed sliders:", slidersArray);
      setSliders(slidersArray);
    } catch (error) {
      console.error("Error loading sliders:", error);
      alert("Failed to load sliders");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title || "");
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("link", formData.link || "");
      if (formData.order) {
        formDataToSend.append("order", formData.order);
      }
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      if (editingSlider) {
        await sliderService.updateSlider(editingSlider.id, formDataToSend);
        alert("Slider updated successfully");
      } else {
        await sliderService.addSlider(formDataToSend);
        alert("Slider added successfully");
      }

      resetForm();
      loadSliders();
    } catch (error) {
      console.error("Error saving slider:", error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save slider"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    const imagePath =
      slider.image || slider.image_url || slider.imageUrl || slider.image_path;
    setFormData({
      title: slider.title || "",
      description: slider.description || "",
      link: slider.link || "",
      order: slider.order || "",
      image: null,
    });
    setImagePreview(imagePath ? getImageUrl(imagePath) : null);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      image: null,
      link: "",
      order: "",
    });
    setImagePreview(null);
    setEditingSlider(null);
    setShowForm(false);
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">
            Slider Management
          </h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            {showForm ? "Cancel" : "Add New Slider"}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-semibold mb-4">
                {editingSlider ? "Edit Slider" : "Add New Slider"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter slider title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter slider description (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link
                  </label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter slider link (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order
                  </label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter display order (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image{" "}
                    {!editingSlider && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={!editingSlider}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {(imagePreview ||
                    (editingSlider &&
                      (editingSlider.image ||
                        editingSlider.image_url ||
                        editingSlider.imageUrl ||
                        editingSlider.image_path))) && (
                    <div className="mt-4">
                      <img
                        src={
                          imagePreview ||
                          getImageUrl(
                            editingSlider.image ||
                              editingSlider.image_url ||
                              editingSlider.imageUrl ||
                              editingSlider.image_path
                          )
                        }
                        alt="Preview"
                        className="max-w-xs h-48 object-cover rounded-lg border border-gray-300 shadow-md"
                        onError={(e) => {
                          // Silently handle error - use local placeholder
                          e.target.src = placeholderImageLarge;
                          e.target.onerror = null; // Prevent infinite loop
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? "Saving..." : editingSlider ? "Update" : "Add"}{" "}
                    Slider
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Sliders List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading && !showForm ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading sliders...</p>
            </div>
          ) : sliders.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No sliders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sliders.map((slider) => (
                    <tr key={slider.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {slider.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {slider.image ||
                        slider.image_url ||
                        slider.imageUrl ||
                        slider.image_path ? (
                          <img
                            src={getImageUrl(
                              slider.image ||
                                slider.image_url ||
                                slider.imageUrl ||
                                slider.image_path
                            )}
                            alt={slider.title || "Slider"}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity shadow-md"
                            onClick={() => {
                              const imgSrc = getImageUrl(
                                slider.image ||
                                  slider.image_url ||
                                  slider.imageUrl ||
                                  slider.image_path
                              );
                              setViewImageModal({
                                show: true,
                                src: imgSrc,
                                title: slider.title || "Slider Image",
                              });
                            }}
                            onError={(e) => {
                              // Silently handle error - use local placeholder
                              e.target.src = placeholderImage;
                              e.target.onclick = null; // Disable click on placeholder
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        ) : (
                          <div className="h-20 w-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500 border border-gray-300">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {slider.title || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        <div
                          className="truncate"
                          title={slider.description || "N/A"}
                        >
                          {slider.description || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {slider.link ? (
                          <a
                            href={slider.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 truncate max-w-xs block"
                          >
                            {slider.link}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {slider.order || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {slider.created_at
                          ? new Date(slider.created_at).toLocaleString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(slider)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Image View Modal */}
        {viewImageModal.show && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() =>
              setViewImageModal({ show: false, src: "", title: "" })
            }
          >
            <div
              className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  {viewImageModal.title}
                </h3>
                <button
                  onClick={() =>
                    setViewImageModal({ show: false, src: "", title: "" })
                  }
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <img
                  src={viewImageModal.src}
                  alt={viewImageModal.title}
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  onError={(e) => {
                    // Silently handle error - use local placeholder
                    e.target.src = placeholderImageLarge;
                    e.target.onerror = null; // Prevent infinite loop
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SliderManagement;
